import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Users, Plus, CheckCircle } from "lucide-react";

export function RosterDialog({ open, onOpenChange, roster, players, rosterSearch, setRosterSearch, selectedRoster, setSelectedRoster, onTogglePlayer, onAddSelected, loading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-white/10 rounded-none max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading text-white text-xl flex items-center gap-2">
            <Users className="h-5 w-5 text-[#E3292E]" /> Player Database
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 flex-1 overflow-hidden">
          <Input
            value={rosterSearch} onChange={(e) => setRosterSearch(e.target.value)}
            placeholder="Search players..." className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#E3292E]"
            data-testid="roster-search-input"
          />
          <div className="flex items-center justify-between">
            <p className="text-white/40 text-xs font-body">
              {selectedRoster.size > 0 ? `${selectedRoster.size} selected` : "Select players to add"}
            </p>
            {roster.length > 0 && (
              <button onClick={() => {
                const currentNames = new Set(players.map((p) => p.name.toLowerCase()));
                const available = roster.filter((r) => !currentNames.has(r.name.toLowerCase()));
                const filtered = available.filter((r) => !rosterSearch || r.name.toLowerCase().includes(rosterSearch.toLowerCase()));
                if (selectedRoster.size === filtered.length) setSelectedRoster(new Set());
                else setSelectedRoster(new Set(filtered.map((r) => r.name)));
              }} className="text-[#E3292E] text-xs hover:text-[#DC2626] transition-colors duration-200" data-testid="roster-select-all">
                {selectedRoster.size > 0 ? "Deselect All" : "Select All"}
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto border border-white/10 min-h-0" style={{ maxHeight: "320px" }}>
            {roster.length === 0 && (
              <div className="p-6 text-center">
                <Users className="w-8 h-8 text-white/10 mx-auto mb-2" />
                <p className="text-white/30 text-sm font-body">No players in database yet</p>
              </div>
            )}
            {roster
              .filter((r) => !rosterSearch || r.name.toLowerCase().includes(rosterSearch.toLowerCase()))
              .map((rp) => {
                const alreadyAdded = players.some((p) => p.name.toLowerCase() === rp.name.toLowerCase());
                const isSelected = selectedRoster.has(rp.name);
                return (
                  <button
                    key={rp.id}
                    disabled={alreadyAdded}
                    onClick={() => !alreadyAdded && onTogglePlayer(rp.name)}
                    className={`w-full text-left px-3 py-2.5 flex items-center justify-between border-b border-white/5 transition-colors duration-200 ${alreadyAdded ? "opacity-40 cursor-not-allowed" : isSelected ? "bg-[#E3292E]/10 border-l-2 border-l-[#E3292E]" : "hover:bg-white/5"}`}
                    data-testid={`roster-player-${rp.name}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 border flex items-center justify-center ${isSelected ? "bg-[#E3292E] border-[#E3292E]" : "border-white/30"} ${alreadyAdded ? "bg-[#309F6A]/30 border-[#309F6A]/50" : ""}`}>
                        {(isSelected || alreadyAdded) && <CheckCircle className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-white text-sm font-body">{rp.name}</span>
                    </div>
                    <span className="text-white/30 text-xs font-body">
                      {alreadyAdded ? "Already added" : `${rp.events_count} event${rp.events_count !== 1 ? "s" : ""}`}
                    </span>
                  </button>
                );
              })}
          </div>
          <Button
            onClick={onAddSelected}
            disabled={selectedRoster.size === 0 || loading}
            className="bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none w-full font-heading font-bold transition-colors duration-200 disabled:opacity-40"
            data-testid="add-from-roster-btn"
          >
            <Plus className="mr-2 h-4 w-4" /> ADD {selectedRoster.size > 0 ? selectedRoster.size : ""} PLAYER{selectedRoster.size !== 1 ? "S" : ""}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
